import React, { useState, cloneElement } from 'react';
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import { FileText, ShoppingBag, CreditCard, Heart, MessageSquare, Settings, LogOut, Download, CalendarDays, Sparkles, Star, AlertCircle, Package, Video, Shield, HelpCircle, Menu, X } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useUser } from '../contexts/UserContext';
import { useLanguage } from '../i18n/LanguageContext';
import { zodiacThemes, getZodiacSign } from '../components/user-dashboard/zodiacThemes';
import { OverviewTab } from '../components/user-dashboard/OverviewTab';
import { ProfileTab } from '../components/user-dashboard/ProfileTab';
import { CalendarTab } from '../components/user-dashboard/CalendarTab';
import { ReportsTab } from '../components/user-dashboard/ReportsTab';
import { OrdersTab } from '../components/user-dashboard/OrdersTab';
import { CreditsTab } from '../components/user-dashboard/CreditsTab';
import { SubscriptionsTab } from '../components/user-dashboard/SubscriptionsTab';
import { FavoritesTab } from '../components/user-dashboard/FavoritesTab';
import { ChatTab } from '../components/user-dashboard/ChatTab';
import { SettingsTab } from '../components/user-dashboard/SettingsTab';
import { PurchasesTab } from '../components/user-dashboard/PurchasesTab';
import { ConsultationsUserTab } from '../components/user-dashboard/ConsultationsUserTab';
import { SecurityTab } from '../components/user-dashboard/SecurityTab';
import { SupportTab } from '../components/user-dashboard/SupportTab';
interface UserDashboardPageProps {
  onNavigate: (page: string) => void;
}
export function UserDashboardPage({
  onNavigate
}: UserDashboardPageProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(() => {
    try {
      return localStorage.getItem('astroguide_avatar_url');
    } catch {
      return null;
    }
  });
  const handleAvatarChange = (url: string | null) => {
    setAvatarUrl(url);
    try {
      if (url) {
        localStorage.setItem('astroguide_avatar_url', url);
      } else {
        localStorage.removeItem('astroguide_avatar_url');
      }
    } catch {}
  };
  const {
    user,
    logout
  } = useUser();
  const {
    t
  } = useLanguage();
  // User Astro Data State
  const [birthData, setBirthData] = useState({
    date: '1990-04-12',
    time: '14:30',
    place: 'Москва, Россия'
  });
  const currentSign = getZodiacSign(birthData.date);
  const theme = zodiacThemes[currentSign];
  // Grouped Menu Structure
  const menuGroups = [{
    title: t('userdash.group.main'),
    items: [{
      id: 'overview',
      label: t('userdash.tab.overview'),
      icon: <FileText className="w-5 h-5" data-id="element-26021" />
    }, {
      id: 'orders',
      label: t('userdash.tab.orders'),
      icon: <ShoppingBag className="w-5 h-5" data-id="element-26022" />
    }, {
      id: 'chat',
      label: t('userdash.tab.chat'),
      icon: <MessageSquare className="w-5 h-5" data-id="element-26023" />
    }]
  }, {
    title: t('userdash.group.astrology'),
    items: [{
      id: 'reports',
      label: t('userdash.tab.reports'),
      icon: <Download className="w-5 h-5" data-id="element-26024" />
    }, {
      id: 'profile',
      label: t('userdash.tab.profile'),
      icon: <Sparkles className="w-5 h-5" data-id="element-26025" />
    }, {
      id: 'consultations',
      label: t('userdash.tab.consultations'),
      icon: <Video className="w-5 h-5" data-id="element-26026" />
    }, {
      id: 'calendar',
      label: t('userdash.tab.calendar'),
      icon: <CalendarDays className="w-5 h-5" data-id="element-26027" />
    }]
  }, {
    title: t('userdash.group.purchases'),
    items: [{
      id: 'purchases',
      label: t('userdash.tab.purchases'),
      icon: <Package className="w-5 h-5" data-id="element-26028" />
    }, {
      id: 'favorites',
      label: t('userdash.tab.favorites'),
      icon: <Heart className="w-5 h-5" data-id="element-26029" />
    }, {
      id: 'credits',
      label: t('userdash.tab.credits'),
      icon: <CreditCard className="w-5 h-5" data-id="element-26030" />
    }, {
      id: 'subscriptions',
      label: t('userdash.tab.subscriptions'),
      icon: <Star className="w-5 h-5" data-id="element-26031" />
    }]
  }, {
    title: t('userdash.group.account'),
    items: [{
      id: 'settings',
      label: t('userdash.tab.settings'),
      icon: <Settings className="w-5 h-5" data-id="element-26032" />
    }, {
      id: 'security',
      label: t('userdash.tab.security'),
      icon: <Shield className="w-5 h-5" data-id="element-26033" />
    }, {
      id: 'support',
      label: t('userdash.tab.support'),
      icon: <HelpCircle className="w-5 h-5" data-id="element-26034" />
    }]
  }];
  // Flatten items for mobile quick scroll if needed, or just use the hamburger menu
  const handleTabChange = (id: string) => {
    setActiveTab(id);
    setMobileMenuOpen(false);
  };
  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab theme={theme} user={user} setActiveTab={setActiveTab} data-id="element-26035" />;
      case 'orders':
        return <OrdersTab theme={theme} onNavigate={onNavigate} setActiveTab={setActiveTab} data-id="element-26036" />;
      case 'chat':
        return <ChatTab theme={theme} data-id="element-26037" />;
      case 'reports':
        return <ReportsTab theme={theme} onNavigate={onNavigate} data-id="element-26038" />;
      case 'profile':
        return <ProfileTab theme={theme} birthData={birthData} setBirthData={setBirthData} setActiveTab={setActiveTab} data-id="element-26039" />;
      case 'consultations':
        return <ConsultationsUserTab theme={theme} onNavigate={onNavigate} setActiveTab={setActiveTab} data-id="element-26040" />;
      case 'calendar':
        return <CalendarTab theme={theme} data-id="element-26041" />;
      case 'purchases':
        return <PurchasesTab onNavigate={onNavigate} data-id="element-26042" />;
      case 'favorites':
        return <FavoritesTab theme={theme} onNavigate={onNavigate} data-id="element-26043" />;
      case 'credits':
        return <CreditsTab theme={theme} onNavigate={onNavigate} data-id="element-26044" />;
      case 'subscriptions':
        return <SubscriptionsTab theme={theme} onNavigate={onNavigate} data-id="element-26045" />;
      case 'settings':
        return <SettingsTab theme={theme} user={user} avatarUrl={avatarUrl} onAvatarChange={handleAvatarChange} data-id="element-26046" />;
      case 'security':
        return <SecurityTab theme={theme} user={user} data-id="element-26047" />;
      case 'support':
        return <SupportTab theme={theme} setActiveTab={setActiveTab} data-id="element-26048" />;
      default:
        return <div className="flex flex-col items-center justify-center py-20 text-slate-500" data-id="element-26049">
            <AlertCircle className="w-12 h-12 mb-4 text-slate-300" data-id="element-26050" />
            <p data-id="element-26051">{t('userdash.sectionInDev')}</p>
          </div>;
    }
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-24 font-sans" data-id="element-26052">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 lg:pt-12" data-id="element-26053">
        {/* Mobile Header & Menu Toggle */}
        <div className="lg:hidden flex items-center justify-between bg-white p-4 rounded-2xl border border-slate-200 shadow-sm mb-6" data-id="element-26054">
          <div className="flex items-center gap-3" data-id="element-26055">
            <div className={`w-10 h-10 rounded-xl ${avatarUrl ? '' : `bg-gradient-to-br ${theme.gradient}`} flex items-center justify-center text-white font-bold overflow-hidden`} data-id="element-26056">
              {avatarUrl ? <img src={avatarUrl} alt="Аватар" className="w-full h-full object-cover" data-id="element-26057" /> : theme.icon}
            </div>
            <div data-id="element-26058">
              <div className="font-bold text-slate-900 text-sm" data-id="element-26059">
                {user?.name || t('userdash.user')}
              </div>
              <div className={`text-xs font-medium ${theme.text}`} data-id="element-26060">
                {theme.name}
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-26061">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-26062" /> : <Menu className="w-6 h-6" data-id="element-26063" />}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-26064">
          {/* Sidebar (Desktop & Mobile Dropdown) */}
          <div className={`lg:col-span-3 ${mobileMenuOpen ? 'block' : 'hidden lg:block'}`} data-id="element-26065">
            <Card padding="none" className="sticky top-24 overflow-hidden shadow-sm mb-6 lg:mb-0" data-id="element-26066">
              {/* Desktop User Info */}
              <div className={`hidden lg:block p-6 border-b ${theme.border} bg-gradient-to-b ${theme.lightGradient}`} data-id="element-26067">
                <div className="flex flex-col items-center text-center" data-id="element-26068">
                  <div className={`w-20 h-20 rounded-full ${avatarUrl ? '' : `bg-gradient-to-br ${theme.gradient}`} flex items-center justify-center text-3xl text-white shadow-md mb-3 relative overflow-hidden`} data-id="element-26069">
                    {avatarUrl ? <img src={avatarUrl} alt="Аватар" className="w-full h-full object-cover" data-id="element-26070" /> : theme.icon}
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center text-xs font-bold text-slate-700 shadow-sm uppercase" data-id="element-26071">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  </div>
                  <div className="font-bold text-slate-900 text-lg" data-id="element-26072">
                    {user?.name || t('userdash.user')}
                  </div>
                  <div className={`text-xs font-bold uppercase tracking-wider mt-1 ${theme.text}`} data-id="element-26073">
                    {theme.name}
                  </div>
                </div>
              </div>

              {/* Grouped Navigation Menu */}
              <nav className="p-3 max-h-[70vh] lg:h-[calc(100vh-300px)] overflow-y-auto custom-scrollbar" data-id="element-26074">
                {menuGroups.map((group, idx) => <div key={idx} className="mb-4 last:mb-0" data-id="element-26075">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-3" data-id="element-26076">
                      {group.title}
                    </div>
                    {group.items.map(item => <button key={item.id} onClick={() => handleTabChange(item.id)} title={item.label} className={`w-full flex items-center px-3 py-2 mb-0.5 rounded-lg text-sm font-medium transition-colors ${activeTab === item.id ? `${theme.bg} ${theme.text} ring-1 ${theme.ring}` : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-26077">
                        <span className={`mr-3 shrink-0 ${activeTab === item.id ? theme.text : 'text-slate-400'}`} data-id="element-26078">
                          {item.icon}
                        </span>
                        <span className="truncate" data-id="element-26079">{item.label}</span>
                      </button>)}
                  </div>)}

                <div className="pt-4 mt-2 border-t border-slate-100" data-id="element-26080">
                  <button onClick={() => {
                  logout();
                  onNavigate('home');
                }} className="w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors" data-id="element-26081">
                    <LogOut className="w-5 h-5 mr-3 shrink-0 text-rose-400" data-id="element-26082" />
                    <span className="truncate" data-id="element-26083">{t('userdash.logout')}</span>
                  </button>
                </div>
              </nav>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className={`lg:col-span-9 ${mobileMenuOpen ? 'hidden lg:block' : 'block'}`} data-id="element-26084">
            <SectionErrorBoundary key={activeTab} sectionName={`user-dashboard-${activeTab}`} fallbackMinHeight="min-h-[400px]" data-id="element-26085">
              {renderContent()}
            </SectionErrorBoundary>
          </div>
        </div>
      </div>
    </div>;
}